// UK Fuel Price Data — Updated 21 September 2026
// Sources: GOV.UK DESNZ Weekly Road Fuel Prices (week commencing 14/09), RAC Fuel Watch,
//          PetrolPrices.co.uk Fuel Price Index (updated 14/09/2026), Fuel Finder UK / CMA Open
//          Data Fuel Finder Scheme (~7,900 stations, live daily read as of 20/09/2026)
// Note: E5 Super Unleaded and SDV Super Diesel are not separately published as national averages.
//       Their figures below are derived by adding the same absolute pence-per-litre premium over


export const FUEL_PRICES = [
  {
    id: 'E10',
    name: 'Petrol',
    full: 'E10 Petrol',
    price: 172.6,
    change: 4.5,
    pct: 2.7,
    avg7: 170.4,
    advice: 'Petrol has been climbing steadily for weeks as the Middle East conflict keeps wholesale prices elevated — the RAC has warned of further rises, so it\u2019s worth filling up sooner rather than waiting for a dip.',
    emoji: '⛽',
  },
  {
    id: 'E5',
    name: 'Super Unleaded',
    full: 'E5 Super Unleaded',
    price: 189.0,
    change: 4.5,
    pct: 2.4,
    avg7: 186.8,
    advice: 'Rising in step with standard petrol — the premium over E10 has held steady, so it\u2019s only worth paying extra if your car needs higher octane or is pre-2011.',
    emoji: '🏎️',
  },
  {
    id: 'B7',
    name: 'Diesel',
    full: 'B7 Diesel',
    price: 196.4,
    change: 5.7,
    pct: 3.0,
    avg7: 193.6,
    advice: 'Diesel has risen faster than petrol in percentage terms this month as the conflict continues to disrupt wholesale supply — prices are now well above where they stood in the June crash.',
    emoji: '🚛',
  },
  {
    id: 'SDV',
    name: 'Super Diesel',
    full: 'SDV Super Diesel',
    price: 215.1,
    change: 5.7,
    pct: 2.7,
    avg7: 212.3,
    advice: 'Tracking standard diesel\u2019s climb — standard diesel remains the better-value choice for most drivers, but high-mileage drivers may still see a case for the additive package here.',
    emoji: '🔴',
  },
];

export const FUEL_TYPES = [
  {
    id: 'E10',
    icon: '⛽',
    name: 'E10 Petrol',
    tag: 'Standard · Most Common',
    desc: 'Standard petrol containing up to 10% bioethanol. Used in the vast majority of petrol cars since 2021. Marginally lower fuel economy than E5 but better for the environment. Cheapest at the pump — the default choice for modern petrol drivers.',
    rating: 3,
    recommended: false,
    pros: ['Cheapest option', 'Widely available', 'Eco-friendlier blend'],
    cons: ['Slightly lower MPG than E5', 'Not compatible with all older/classic cars'],
  },
  {
    id: 'E5',
    icon: '🏎️',
    name: 'E5 Super Unleaded',
    tag: 'Premium Petrol · High Octane',
    desc: 'Higher octane petrol (95–98 RON) with up to 5% bioethanol. Better for performance cars and those requiring higher octane. Provides cleaner combustion and can improve MPG on suitable vehicles. Also the safe choice for pre-2011 or classic cars.',
    rating: 4,
    recommended: true,
    pros: ['Higher octane = more power', 'Cleaner engine over time', 'Safe for all petrol cars'],
    cons: ['More expensive than E10', 'Not necessary for most modern standard cars'],
  },
  {
    id: 'B7',
    icon: '🚛',
    name: 'Diesel B7',
    tag: 'Standard Diesel',
    desc: 'Standard diesel fuel with up to 7% biodiesel (FAME). Essential for diesel-engine vehicles. More energy-dense than petrol, offering better long-distance economy. Higher pump price than petrol but lower cost-per-mile on motorways.',
    rating: 3,
    recommended: false,
    pros: ['Better long-distance MPG', 'Suits larger vehicles & vans', 'Energy-dense fuel'],
    cons: ['Most expensive conventional fuel', 'Higher NOx emissions in urban areas'],
  },
  {
    id: 'SDV',
    icon: '🔴',
    name: 'Super Diesel',
    tag: 'Premium Diesel',
    desc: 'Premium diesel with enhanced additive packages for cleaning injectors and improved combustion efficiency. Recommended for high-mileage diesel drivers and performance diesel vehicles. Worth it if you cover 20,000+ miles/year.',
    rating: 4,
    recommended: false,
    pros: ['Cleans fuel injectors', 'Slightly better MPG on suitable engines', 'Good for high-mileage diesels'],
    cons: ['Significantly more expensive', 'Marginal benefit for low-mileage drivers'],
  },
  {
    id: 'EV',
    icon: '⚡',
    name: 'Electric (EV)',
    tag: 'Zero Emissions · Future-Proof',
    desc: 'Electric vehicles use no combustion fuel. Per-mile cost is significantly lower than petrol or diesel — especially with home charging. Zero tailpipe emissions. Rapid charging infrastructure expanding rapidly across UK. Best for urban and moderate-mileage drivers.',
    rating: 5,
    recommended: false,
    pros: [
      'Lowest running cost per mile (~42p/L equiv.)',
      'Zero tailpipe emissions',
      'Government EV incentives available',
    ],
    cons: ['Higher upfront vehicle cost', 'Charging time vs. 5-min refuel', 'Range limitations on long trips'],
  },
];

export const INSIGHTS = [
  {
    icon: '📉',
    label: 'Best Time to Fill',
    value: 'Mon–Tue',
    sub: 'Mid-week prices typically 1–2p lower; avoid weekends as demand peaks',
  },
  {
    icon: '📍',
    label: 'Cheapest Region',
    value: 'N. Ireland',
    sub: 'Continuing to offer the lowest averages — generally well below the UK average',
  },
  {
    icon: '🔮',
    label: 'Price Forecast',
    value: 'Rising',
    sub: 'The RAC has warned of further rises as the 2026 Middle East conflict keeps wholesale prices elevated — both fuels are up sharply since the June low',
  },
  {
    icon: '🏪',
    label: 'Cheapest Brand',
    value: 'Supermarkets',
    sub: 'Supermarkets remain the cheapest major brand group, typically several pence per litre below premium forecourts',
  },
  {
    icon: '⚡',
    label: 'EV Cost Equiv.',
    value: '~42p/L',
    sub: 'Based on ~24.5p/kWh home charging vs 172.6p avg petrol (September 2026)',
  },
  {
    icon: '💷',
    label: 'Fuel Duty',
    value: '52.95p — rise expected',
    sub: 'Duty has been held at 52.95p/L, but a rise is expected after September 2026; exact schedule not yet confirmed',
  },
];

export const TICKER_ITEMS = [
  'E10 Petrol · 172.6p/L — climbing, up 4.5p on the week',
  'E5 Super · 189.0p/L — tracking petrol upwards, up 4.5p',
  'B7 Diesel · 196.4p/L — up 5.7p as wholesale prices stay elevated',
  'SDV Super Diesel · 215.1p/L — up 5.7p, tracking standard diesel',
  'UK Avg Updated Every 30 Min · 7,900+ Stations Live',
  'RAC Warns of Further Rises Amid 2026 Middle East Conflict',
  'Supermarkets Remain the Cheapest Major Brand Group at the Pump',
  'All UK Stations Now Required to Report Prices — CMA Fuel Finder Scheme',
];